import type {
  MissionAssessmentCoreSelectDto,
  MissionAssessmentListItemDto,
  TechnologySelectDto,
} from '@@types/api.types';
import {
  AssessmentSubtype,
  AssessmentType,
  MissionAssessmentRequestStatus,
  SurveyStatus,
  UserProfileRole,
} from '@@types/api.types';
import { MaContextColumn } from '@components/pages/mission-assessment/components/ma-context-column.component';
import { StickyColumn } from '@components/shared/sticky-column.component';
import { SurveyCTA } from '@components/shared/survey-cta.component';
import { Tile } from '@components/shared/tile.component';
import { useSurveyNavigation } from '@hooks/surveys.hook';
import { errorToast } from '@hooks/toast.hook';
import { STICKY_COLUMN_CLASSES, STICKY_CONTAINER_STYLE } from '@lib/sticky.utils';
import { cn } from '@lib/utils';
import { useAssessmentsQueryOptions } from '@queries/assessment.query';
import { useGetLatestMissionAssessmentRequestForTechnology } from '@queries/mission-assessment-requests.query';
import {
  useDuplicateMissionAssessmentAsDraft,
  useMissionAssessmentServicesQueryOptions,
} from '@queries/mission-assessments.query';
import { useGetTechnologySurveys, useInnovatorCreateSurvey } from '@queries/survey.query';
import { useGetCurrentUser } from '@queries/users.query';
import { useQuery } from '@tanstack/react-query';
import { isChildOrganizationView as getIsChildOrganizationView } from '@utils/parent-organization.util';
import { parseAsString, useQueryState } from 'nuqs';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaOverviewContent } from './ma/overview/ma-overview-content.component';
import { MaAssessmentHistoryList } from './ma/sidebar/ma-assessment-history-list.component';
import { MaExpertAssessmentList } from './ma/sidebar/ma-expert-assessment-list.component';
import { scrollToNavOrTop } from './shared/scroll.utils';
import {
  TECHNOLOGY_TAB_SIDEBAR_WIDTH_CLASS,
  TECHNOLOGY_TAB_STACK_LAYOUT_CLASS,
  TECHNOLOGY_TAB_STICKY_CLASS,
} from './shared/technology-container-query.constants';
import { TechnologyMissionAssessmentRequestModal } from './technology-mission-assessment-request-modal.component';
import { TechnologyMissionAssessmentTabs } from './technology-mission-assessment-tabs.component';

export type MaSectionTab = 'all' | 'effects' | 'expertNotes' | 'hurdles' | 'interoperability' | 'services';

type TechnologyMissionAssessmentComponentProps = {
  isLoading?: boolean;
  isTransitioning?: boolean;
  isVersionsLoading?: boolean;
  missionAssessment?: MissionAssessmentCoreSelectDto | null;
  onNavigateToTab?: (tab: string) => void;
  technology?: TechnologySelectDto;
  versions?: MissionAssessmentListItemDto[];
};

const defaultSection: MaSectionTab = 'all';

export const TechnologyMissionTab: FC<TechnologyMissionAssessmentComponentProps> = (props) => {
  const {
    isLoading,
    isTransitioning,
    isVersionsLoading,
    missionAssessment,
    onNavigateToTab,
    technology,
    versions = [],
  } = props;

  const [selectedSection, setSelectedSection] = useState<MaSectionTab>(defaultSection);
  const [missionAssessmentRequestModalOpen, setMissionAssessmentRequestModalOpen] = useState(false);
  const stableMissionAssessmentRef = useRef<MissionAssessmentCoreSelectDto | null>(missionAssessment ?? null);
  const missionSectionNavAnchorRef = useRef<HTMLDivElement>(null);
  const sidebarNavAnchorRef = useRef<HTMLDivElement>(null);
  const previousIsTransitioningRef = useRef<boolean>(isTransitioning ?? false);
  const navigate = useNavigate();
  const { navigateToSurvey } = useSurveyNavigation();

  const [maId, setMaId] = useQueryState('maId', parseAsString);

  const { data: user, isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();
  const { data: latestMissionAssessmentRequest } = useGetLatestMissionAssessmentRequestForTechnology(technology?.id);
  const { mutateAsync: duplicateMissionAssessment, isPending: isDuplicatingMissionAssessment } =
    useDuplicateMissionAssessmentAsDraft();
  const { mutateAsync: createSurvey, isPending: isCreatingSurvey } = useInnovatorCreateSurvey();

  const technologyId = technology?.id ?? missionAssessment?.technology?.id ?? null;
  const isChildOrganizationView = getIsChildOrganizationView({
    isSelectedCompanyParentOrganization,
    selectedCompanyId: selectedCompany?.id,
    technologyCompanyId: technology?.company?.id ?? missionAssessment?.technology?.company?.id,
  });

  const assessmentsQueryOptions = useAssessmentsQueryOptions({
    technologyId: technologyId ?? undefined,
    type: [AssessmentType.TRL],
  });
  const { data: assessments = [] } = useQuery(assessmentsQueryOptions);

  // Fetch MA services for Military TRL button
  const { data: maServices } = useQuery(useMissionAssessmentServicesQueryOptions(missionAssessment?.id));

  // Check for existing Military TRL draft surveys
  const { data: draftSurveys = [] } = useGetTechnologySurveys({
    status: SurveyStatus.DRAFT,
    technologyId: technologyId ?? undefined,
  });
  const draftMilitaryTrlSurvey = draftSurveys.find(
    (survey) => survey.assessmentType === AssessmentType.TRL && survey.assessmentSubtype === AssessmentSubtype.MILITARY,
  );
  const hasExistingMilitaryTrlAssessment = useMemo(() => {
    return assessments.some(
      (assessment) => assessment.type === AssessmentType.TRL && assessment.subtype === AssessmentSubtype.MILITARY,
    );
  }, [assessments]);
  const hasExistingMilitaryTrlDraft = Boolean(draftMilitaryTrlSurvey);

  const openMissionAssessmentRequestModal = () => setMissionAssessmentRequestModalOpen(true);
  const closeMissionAssessmentRequestModal = () => setMissionAssessmentRequestModalOpen(false);

  useEffect(() => {
    if (!isTransitioning && missionAssessment) {
      stableMissionAssessmentRef.current = missionAssessment;
    }
  }, [isTransitioning, missionAssessment]);

  useEffect(() => {
    const wasTransitioning = previousIsTransitioningRef.current;
    const isNowTransitioning = isTransitioning ?? false;

    if (wasTransitioning && !isNowTransitioning) {
      scrollToNavOrTop({ anchorElement: sidebarNavAnchorRef.current });
    }

    previousIsTransitioningRef.current = isNowTransitioning;
  }, [isTransitioning]);

  const userRole = user?.profile?.role;
  const isExpert = userRole === UserProfileRole.EXPERT;
  const isInnovator = userRole === UserProfileRole.INNOVATOR;
  const isIntelligence = userRole === UserProfileRole.INTELLIGENCE;
  const canRequestAssessment = isInnovator && !isChildOrganizationView;

  const displayMissionAssessment = isTransitioning ? stableMissionAssessmentRef.current : missionAssessment;

  // Only allow duplicate when current user is the expert who created this assessment
  const canDuplicateAssessment = isExpert && displayMissionAssessment?.expert?.id === user?.id;

  // Military TRL button conditions - use MA Request's use and domain
  const maRequestUse = displayMissionAssessment?.request?.use;
  const maRequestDomain = displayMissionAssessment?.request?.domain;
  const taskRecommendations = maServices?.taskRecommendations ?? [];
  const recommendedServices = maServices?.recommendedServices ?? [];

  const hasRequestUseDomain = Boolean(maRequestUse && maRequestDomain);
  const hasTaskRecommendations = taskRecommendations.length > 0;
  const hasRecommendedServices = recommendedServices.length > 0;
  const hasBothTasksAndServices = hasTaskRecommendations && hasRecommendedServices;
  const canStartMilitaryTrl = hasRequestUseDomain && hasBothTasksAndServices && !hasExistingMilitaryTrlDraft;

  const militaryTrlDisabledReason = useMemo(() => {
    if (hasExistingMilitaryTrlDraft) {
      return 'A draft Military TRL assessment already exists for this technology.';
    }
    const reasons: string[] = [];
    if (!hasRequestUseDomain) {
      reasons.push('MA Request must have Use and Domain');
    }
    if (!hasTaskRecommendations) {
      reasons.push('Task Recommendations are required');
    }
    if (!hasRecommendedServices) {
      reasons.push('Recommended Services are required');
    }
    return reasons.join('. ');
  }, [hasExistingMilitaryTrlDraft, hasRequestUseDomain, hasTaskRecommendations, hasRecommendedServices]);

  const handleStartMilitaryTrl = async (): Promise<void> => {
    if (!displayMissionAssessment?.id || !technologyId) {
      errorToast({ description: 'Mission Assessment or Technology ID is missing.' });
      return;
    }

    try {
      const survey = await createSurvey({
        assessmentSubtype: AssessmentSubtype.MILITARY,
        assessmentType: AssessmentType.TRL,
        missionAssessmentId: displayMissionAssessment.id,
        technologyId,
      });
      navigateToSurvey(`/technology/${technologyId}/survey/${survey.id}`);
    } catch (error) {
      if (error instanceof Error) {
        errorToast({ description: `Failed to create Military TRL survey: ${error.message}` });
      }
    }
  };

  const hasNoMA = !displayMissionAssessment;
  const hasOpenMARequest =
    latestMissionAssessmentRequest?.status === MissionAssessmentRequestStatus.IN_PROGRESS ||
    latestMissionAssessmentRequest?.status === MissionAssessmentRequestStatus.REQUESTED;
  const isOverviewSelected = Boolean(technology) && !maId;

  // Get selected assessment ID from URL or default to latest
  const selectedAssessmentId = useMemo(() => {
    return displayMissionAssessment?.id ?? null;
  }, [displayMissionAssessment]);

  const handleSectionChange = (section: MaSectionTab) => {
    setSelectedSection(section);
    scrollToNavOrTop({ anchorElement: missionSectionNavAnchorRef.current });
  };

  const handleSelectAssessment = (assessmentId: string) => {
    setSelectedSection(defaultSection);
    void setMaId(assessmentId);
  };

  const handleSelectOverview = () => {
    setSelectedSection(defaultSection);
    void setMaId(null);
    scrollToNavOrTop({ anchorElement: sidebarNavAnchorRef.current });
  };

  const handleDuplicate = async () => {
    if (!displayMissionAssessment?.id || !displayMissionAssessment?.technology?.id) {
      errorToast({ description: 'Cannot duplicate: Mission Assessment ID or Technology ID is missing.' });
      return;
    }
    try {
      const newlyCreatedDraft = await duplicateMissionAssessment({
        missionAssessmentId: displayMissionAssessment.id,
        technologyId: displayMissionAssessment.technology.id,
      });
      navigate(`/technology/${displayMissionAssessment.technology.id}/assessment/${newlyCreatedDraft.id}/edit`);
    } catch (error) {
      if (error instanceof Error) {
        errorToast({ description: `Failed to create a new draft: ${error.message}` });
      }
    }
  };

  // Only block on essential MA loading, not secondary data
  if (isLoading) {
    return (
      <Tile
        className="min-h-[120px]"
        isLoading
      />
    );
  }

  // Always show sidebar + main content layout when we have technologyId
  if (!technologyId) {
    return null;
  }

  // Expert/Intel: full-width empty state when no MAs exist
  if ((isExpert || isIntelligence) && !isVersionsLoading && versions.length === 0) {
    return (
      <Tile
        className="min-h-64"
        emptyState={{ title: 'No Mission Assessments' }}
        isEmpty
      />
    );
  }

  // Sidebar visible for all roles when technology exists
  const showSidebar = Boolean(technology);
  return (
    <>
      <div
        aria-hidden="true"
        className="h-0"
        ref={sidebarNavAnchorRef}
      />

      <div
        className={cn(
          'flex gap-4',
          TECHNOLOGY_TAB_STACK_LAYOUT_CLASS,
          isTransitioning && 'opacity-60 pointer-events-none',
          'transition-opacity duration-200',
        )}
        style={STICKY_CONTAINER_STYLE}
      >
        {/* Sidebar */}
        {showSidebar && (
          <StickyColumn
            className={cn(TECHNOLOGY_TAB_SIDEBAR_WIDTH_CLASS, TECHNOLOGY_TAB_STICKY_CLASS)}
            isSticky
          >
            {/* Military TRL CTA - Innovator only */}
            {isInnovator && displayMissionAssessment && !isChildOrganizationView && (
              <SurveyCTA
                disabled={!canStartMilitaryTrl}
                disabledReason={militaryTrlDisabledReason}
                assessmentKind="militaryTrl"
                hasExistingScore={hasExistingMilitaryTrlAssessment}
                loading={isCreatingSurvey}
                onStartClick={handleStartMilitaryTrl}
                survey={draftMilitaryTrlSurvey}
                technology={technology}
              />
            )}

            {/* Assessment History List - Innovator/Intelligence see all experts' assessments */}
            {(isInnovator || isIntelligence) && (
              <MaAssessmentHistoryList
                isLoading={isVersionsLoading}
                isOverviewActive={isOverviewSelected}
                onSelectAssessment={handleSelectAssessment}
                onSelectOverview={handleSelectOverview}
                selectedAssessmentId={selectedAssessmentId}
                versions={versions}
              />
            )}

            {/* Expert Assessment List - Expert sees only their own assessments */}
            {isExpert && (
              <MaExpertAssessmentList
                isLoading={isVersionsLoading}
                isOverviewActive={isOverviewSelected}
                onSelectAssessment={handleSelectAssessment}
                onSelectOverview={handleSelectOverview}
                selectedAssessmentId={selectedAssessmentId}
                versions={versions}
              />
            )}

            {/* Context Column in sidebar - visible below 2xl breakpoint, hidden at 2xl+ */}
            {!isOverviewSelected && (
              <div className="2xl:hidden">
                <MaContextColumn
                  canDuplicate={canDuplicateAssessment}
                  expert={displayMissionAssessment?.expert}
                  isDuplicating={isDuplicatingMissionAssessment}
                  onDuplicate={handleDuplicate}
                  onNavigateToTab={onNavigateToTab}
                  requestId={displayMissionAssessment?.request?.id}
                  technology={technology}
                  technologyId={technologyId}
                />
              </div>
            )}
          </StickyColumn>
        )}

        {/* Main Content - also sticky so shorter content sticks when taller sidebar scrolls */}
        <StickyColumn
          className="flex-1 min-w-0"
          isSticky={false}
        >
          <div className="flex flex-col gap-4">
            {isOverviewSelected && technology ? (
              <MaOverviewContent
                canRequestAssessment={canRequestAssessment}
                onOpenRequestModal={openMissionAssessmentRequestModal}
                onSelectAssessment={handleSelectAssessment}
                technology={technology}
                versions={versions}
              />
            ) : hasNoMA ? (
              <Tile
                className="min-h-64"
                emptyState={{
                  message: canRequestAssessment && !hasOpenMARequest ? 'Request Assessment' : undefined,
                  onClick: canRequestAssessment && !hasOpenMARequest ? openMissionAssessmentRequestModal : undefined,
                  title: 'No Mission Assessment',
                }}
                isEmpty
              />
            ) : (
              displayMissionAssessment && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                  <div
                    aria-hidden="true"
                    className="h-0"
                    ref={missionSectionNavAnchorRef}
                  />
                  <TechnologyMissionAssessmentTabs
                    missionAssessment={displayMissionAssessment}
                    missionAssessmentId={displayMissionAssessment.id}
                    onTabChange={handleSectionChange}
                    renderContentOnly
                    selectedTab={selectedSection}
                    technologyId={technologyId}
                  />
                </div>
              )
            )}
          </div>
        </StickyColumn>

        {/* Context Column - visible at 2xl+ breakpoint, hidden below */}
        {!isOverviewSelected && (
          <StickyColumn
            className={cn(STICKY_COLUMN_CLASSES, TECHNOLOGY_TAB_STICKY_CLASS, 'hidden 2xl:block')}
            isSticky
          >
            <MaContextColumn
              canDuplicate={canDuplicateAssessment}
              expert={displayMissionAssessment?.expert}
              isDuplicating={isDuplicatingMissionAssessment}
              onDuplicate={handleDuplicate}
              onNavigateToTab={onNavigateToTab}
              requestId={displayMissionAssessment?.request?.id}
              technology={technology}
              technologyId={technologyId}
            />
          </StickyColumn>
        )}
      </div>

      {/* Modal */}
      {isInnovator && technology && (
        <TechnologyMissionAssessmentRequestModal
          isOpen={missionAssessmentRequestModalOpen}
          onClose={closeMissionAssessmentRequestModal}
          technology={technology}
        />
      )}
    </>
  );
};
